"use client";

import React, { useEffect, useState } from "react";
import { MessageSquareText, Quote, User } from "lucide-react";

import { Typography } from "@/components/ui";

export type TestimonialItem = {
  text: string;
  name: string;
  role: string;
};

type TestimonialsScrollerProps = {
  items: TestimonialItem[];
};

export function TestimonialsScroller({ items }: TestimonialsScrollerProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const active = items[activeIndex] ?? items[0];

  useEffect(() => {
    if (items.length <= 1) return;

    const timer = window.setInterval(() => {
      setActiveIndex(current => (current + 1) % items.length);
    }, 6000);

    return () => window.clearInterval(timer);
  }, [items.length]);

  if (!active) return null;

  return (
    <div className="mx-auto w-full max-w-5xl">
      <div className="grid w-full grid-cols-1 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm lg:grid-cols-[1.3fr_1fr]">
        <div className="flex min-w-0 flex-col justify-center p-8 md:p-12">
          <Quote
            className="mb-4 h-10 w-10 shrink-0 text-secondary/20"
            fill="currentColor"
            aria-hidden="true"
          />

          <div className="mb-3 text-base tracking-wider text-amber-400">★★★★★</div>

          <div
            key={activeIndex}
            role="region"
            aria-label={`Testimonial ${activeIndex + 1} of ${items.length}`}
            aria-live="polite"
            className="min-w-0"
          >
            <Typography
              variant="body"
              as="blockquote"
              className="mb-6 max-w-full whitespace-normal break-words italic leading-relaxed text-text-primary"
            >
              &quot;{active.text}&quot;
            </Typography>

            <div className="flex items-center gap-3 border-t border-gray-100 pt-5">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-secondary/10 text-secondary">
                <User className="h-5 w-5" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <Typography
                  variant="body"
                  as="p"
                  className="text-sm font-semibold text-text-primary"
                >
                  {active.name}
                </Typography>
                <Typography variant="body" as="p" className="text-xs text-text-secondary">
                  {active.role}
                </Typography>
              </div>
            </div>
          </div>

          {items.length > 1 ? (
            <div
              className="mt-8 flex items-center gap-2"
              role="tablist"
              aria-label="Choose testimonial"
            >
              {items.map((item, index) => (
                <button
                  key={`${item.name}-${index}`}
                  type="button"
                  role="tab"
                  aria-selected={index === activeIndex}
                  aria-label={`Show testimonial ${index + 1}`}
                  onClick={() => setActiveIndex(index)}
                  className={`h-2 rounded-full transition-all ${
                    index === activeIndex
                      ? "w-6 bg-secondary"
                      : "w-2 bg-gray-300 hover:bg-gray-400"
                  }`}
                />
              ))}
            </div>
          ) : null}
        </div>

        <div className="relative hidden items-center justify-center bg-gradient-to-br from-secondary/5 to-transparent p-10 lg:flex">
          <div className="relative flex h-64 w-64 items-center justify-center rounded-full border border-secondary/10 bg-white/60">
            <span
              className="absolute -top-1 right-6 h-3 w-3 rounded-full bg-secondary"
              aria-hidden="true"
            />
            <span
              className="absolute -bottom-1 left-6 h-3 w-3 rounded-full bg-secondary/40"
              aria-hidden="true"
            />
            <div className="flex h-28 w-28 items-center justify-center rounded-2xl bg-gradient-to-br from-secondary to-primary shadow-lg">
              <MessageSquareText className="h-12 w-12 text-white" aria-hidden="true" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
